import React, { useEffect, useState } from "react";
import UsersService from "../services/UsersService";
import Button from "./ui/Button";
import { FormControl, FormGroup } from "./ui/Modal";
import { normalizeMobile } from "../utility/mobileNumber";

const errorMessageOf = (error, fallback) => {
  const data = error?.response?.data;
  return data?.message || (typeof data === "string" ? data : null) || fallback;
};

// "Log in with a mobile OTP": enter the registered number -> we SMS a code -> enter it. Used inside
// LoginPopup in place of the phone + password fields, only when the admin has switched OTP login
// on (see /admin/settings). The server decides who may use it (existing, active, non-admin
// accounts of the chosen type) and answers with the same session as a password login.
const OtpLoginForm = ({ accountType, otpLength = 6, onSuccess }) => {
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const requestOtp = async () => {
    const normalized = normalizeMobile(phone);
    if (!normalized) {
      setError("Enter a valid 10-digit mobile number.");
      return false;
    }
    setError("");
    setBusy(true);
    try {
      const response = await UsersService.sendLoginOtp(normalized, accountType);
      setNotice(response.data?.message || "OTP sent.");
      setResendIn(response.data?.resendInSeconds || 30);
      setOtp("");
      setStep("code");
      return true;
    } catch (e) {
      setError(errorMessageOf(e, "Couldn't send the OTP. Please try again."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitPhone = (event) => {
    event.preventDefault();
    requestOtp();
  };

  const submitCode = async (event) => {
    event.preventDefault();
    if (otp.length !== otpLength) {
      setError(`Enter the ${otpLength}-digit OTP.`);
      return;
    }
    setError("");
    setBusy(true);
    try {
      const response = await UsersService.verifyLoginOtp(normalizeMobile(phone), otp, accountType);
      onSuccess(response.data);
    } catch (e) {
      setError(errorMessageOf(e, "Couldn't verify the OTP. Please try again."));
      setOtp("");
    } finally {
      setBusy(false);
    }
  };

  if (step === "code") {
    return (
      <form onSubmit={submitCode} data-testid="otp-code-step">
        <p className="mt-3 text-sm text-gray-600">{notice}</p>
        <FormGroup label="Enter OTP" htmlFor="loginOtp" error={error}>
          <FormControl
            id="loginOtp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={otpLength}
            placeholder={`${otpLength}-digit OTP`}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, otpLength))}
            isInvalid={error !== ""}
            className="tracking-widest"
          />
        </FormGroup>
        <Button type="submit" variant="primary" disabled={busy} className="mt-4 w-full">
          {busy ? "Please wait…" : "Verify & Log In"}
        </Button>
        <div className="mt-3 flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-brand-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={busy || resendIn > 0}
            onClick={requestOtp}
          >
            {resendIn > 0 ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
          </button>
          <button
            type="button"
            className="text-gray-500 hover:underline"
            onClick={() => {
              setStep("phone");
              setError("");
              setOtp("");
            }}
          >
            Change number
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submitPhone} data-testid="otp-phone-step">
      <FormGroup label="Mobile number" htmlFor="otpPhone" error={error} className="!mt-3">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
            +91
          </span>
          <FormControl
            id="otpPhone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder="Registered mobile number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            isInvalid={error !== ""}
            className="pl-11"
          />
        </div>
      </FormGroup>
      <Button type="submit" variant="primary" disabled={busy} className="mt-4 w-full">
        {busy ? "Sending…" : "Send OTP"}
      </Button>
    </form>
  );
};

export default OtpLoginForm;
